import { FC, useEffect, useState } from "react";
import axios from "axios";
import { ChevronRight, DollarSign, Percent } from "lucide-react";
import { KeyboardArrowDown } from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";

import { Colors, GET_BILL_BY_ID } from "../../../configs";
import { pxToRem } from "../../../util";
import { CustomButton } from "../../../components/atoms";
import CustomRadioButton from "../../../components/atoms/radio-button/custom-radio-button";
import { NewCustomModalDrawer } from "../../../components/atoms/custom-drawer/new-custom-modal-drawer";
import { useAppDispatch, useAppSelector } from "../../../hooks";
import { updateBillDiscountAction } from "../../../redux/actions/cartAction";
import { BaseResponseSingle, DiscountMode, IDiscountResponse } from "../../../typings";
import { IBillResponse } from "../../../typings/interfaces/responses/new-bill-response";
import { CartItem } from "../../../typings/interfaces/pos/cart-item";
import { calculateCheckoutDiscount } from "../../../util/discount-calc";
import ManagerCodeModal from "../../../components/common/manager-code-modal/ManagerCodeModal";
import { useSnackbarContext } from "../../../providers/SnackbarProvider";

interface IDiscountDrawerBill {
  id: number;
  // Original subtotal, before any discount
  subtotal: number;
  // The bill's lines, so a predefined discount covers only its services
  items: CartItem[];
  // Check-in discount as saved in the bill's billString; newer bills keep
  // the discount's name in details.
  checkInDiscount?: { id?: number | null; details?: { discountName?: string } | null } | null;
}

// A bill's discount split into its check-in and checkout parts.
interface IBillDiscountSplit {
  checkInAmount: number;
  checkoutAmount: number;
  checkoutDiscountId: number | null;
}

interface IDiscountDrawerProps {
  isOpen: boolean;
  toggleDrawer: () => void;
  containerRef?: HTMLElement | null;
  bills: IDiscountDrawerBill[];
  // Selected bills left out because something is already paid on them.
  paidBillsSkipped?: number;
}

const NONE_DISCOUNT_ID = "none";

// Up to two decimal places, no sign - the field only ever holds a positive amount.
const AMOUNT_PATTERN = /^\d*\.?\d{0,2}$/;

const formatMoney = (amount: number) => `$${amount.toFixed(2)}`;

const splitOf = (bill: IBillResponse): IBillDiscountSplit => {
  const checkout = bill.checkoutDiscountAmount ?? 0;
  return {
    checkInAmount: Math.max(0, (bill.discountAmount ?? 0) - checkout),
    checkoutAmount: checkout,
    checkoutDiscountId: bill.checkoutDiscountId ?? null,
  };
};


const DiscountDrawer: FC<IDiscountDrawerProps> = ({
  isOpen,
  toggleDrawer,
  containerRef,
  bills,
  paidBillsSkipped = 0,
}) => {
  const dispatch = useAppDispatch();
  const snackbar = useSnackbarContext();
  const { getDiscountByStoreResponse } = useAppSelector(
    (state) => state.discount
  );
  const { user } = useAppSelector((state) => state.auth);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);

  const [discountType, setDiscountType] = useState<"defined" | "custom">(
    "defined"
  );
  const [definedDiscountId, setDefinedDiscountId] =
    useState<string>(NONE_DISCOUNT_ID);
  const [customDiscountType, setCustomDiscountType] = useState<
    "percentage" | "fixed"
  >("percentage");
  const [customAmount, setCustomAmount] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);
  // Check-in / checkout split per bill. The bill list only has the total, so
  // each bill is fetched when the drawer opens.
  const [splits, setSplits] = useState<Record<number, IBillDiscountSplit>>({});
  const [splitsError, setSplitsError] = useState(false);
  const billIdsKey = bills.map((bill) => bill.id).join(",");

  useEffect(() => {
    if (!isOpen || !billIdsKey) return;
    let cancelled = false;
    setSplitsError(false);
    Promise.all(
      billIdsKey.split(",").map((id) =>
        axios.get<BaseResponseSingle<IBillResponse>>(GET_BILL_BY_ID(id))
      )
    )
      .then((responses) => {
        if (cancelled) return;
        const next: Record<number, IBillDiscountSplit> = {};
        responses.forEach((response) => {
          const bill = response.data?.body;
          if (bill) next[bill.id] = splitOf(bill);
        });
        setSplits(next);
      })
      .catch(() => {
        if (!cancelled) setSplitsError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, billIdsKey]);

  const definedDiscounts =
    getDiscountByStoreResponse?.body.filter((d) => d.enabled) ?? [];
  const selectedDefinedDiscount = definedDiscounts.find(
    (d) => d.id === definedDiscountId
  );

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setDiscountType(event.target.value as "defined" | "custom");
  };

  const handleDefinedDiscountChange = (e: SelectChangeEvent<unknown>) => {
    setDefinedDiscountId(e.target.value as string);
  };

  const handleCustomDiscountTypeChange = (e: SelectChangeEvent<unknown>) => {
    setCustomDiscountType(e.target.value as "percentage" | "fixed");
    setCustomAmount("");
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (!AMOUNT_PATTERN.test(value)) return;
    if (customDiscountType === "percentage" && Number(value) > 100) {
      setCustomAmount("100");
      return;
    }
    setCustomAmount(value);
  };

  const canSubmit =
    bills.length > 0 &&
    !submitting &&
    (discountType === "defined"
      ? !!selectedDefinedDiscount
      : Number(customAmount) > 0);

  // Every checkout discount needs a fresh manager approval.
  const onClickAddDiscount = () => {
    if (!canSubmit) return;
    setIsAccessModalOpen(true);
  };

  const handleAddDiscount = async () => {
    if (!canSubmit) return;

    let mode: DiscountMode;
    let amount: number;
    let discountId: number | null;
    let serviceIds: number[] | null = null;
    let processingType: IDiscountResponse["processingType"] = "ALL";

    if (discountType === "defined" && selectedDefinedDiscount) {
      mode = selectedDefinedDiscount.discountMode;
      amount = selectedDefinedDiscount.discountAmount;
      discountId = Number(selectedDefinedDiscount.id);
      serviceIds = selectedDefinedDiscount.serviceIds ?? [];
      processingType = selectedDefinedDiscount.processingType ?? "REGULAR";
    } else {
      mode =
        customDiscountType === "fixed"
          ? DiscountMode.FIXED
          : DiscountMode.PERCENTAGE;
      amount = Number(customAmount);
      discountId = null;
    }

    // A bill with nothing the discount covers is left alone, so its earlier
    // checkout discount (if any) isn't replaced by $0.
    const updates = bills
      .map((bill) => ({
        billId: bill.id,
        discountAmount: calculateCheckoutDiscount(
          bill.items,
          bill.subtotal,
          serviceIds,
          mode,
          amount,
          processingType
        ),
      }))
      .filter((u) => u.discountAmount > 0);

    if (updates.length === 0) {
      snackbar.showSnackbar(
        "This discount doesn't cover any items on the selected bill(s)",
        "error"
      );
      return;
    }

    setSubmitting(true);
    try {
      const updated = await Promise.all(
        updates.map((u) =>
          dispatch(
            updateBillDiscountAction({ ...u, discountId, checkout: true })
          ).unwrap()
        )
      );
      setSplits((prev) => {
        const next = { ...prev };
        updated.forEach((bill) => {
          if (bill) next[bill.id] = splitOf(bill);
        });
        return next;
      });
      snackbar.showSnackbar("Discount applied", "success");
      setDefinedDiscountId(NONE_DISCOUNT_ID);
      setCustomAmount("");
      toggleDrawer();
    } catch (err: any) {
      snackbar.showSnackbar(
        typeof err === "string" ? err : "Failed to apply discount",
        "error"
      );
    } finally {
      setSubmitting(false);
    }
  };

  // Discount the selected predefined discount would add, shown before it's
  // applied (a percentage is shown as the dollar amount it works out to).
  const definedPreview = selectedDefinedDiscount
    ? bills.reduce(
        (acc, bill) =>
          acc +
          calculateCheckoutDiscount(
            bill.items,
            bill.subtotal,
            selectedDefinedDiscount.serviceIds ?? [],
            selectedDefinedDiscount.discountMode,
            selectedDefinedDiscount.discountAmount,
            selectedDefinedDiscount.processingType ?? "REGULAR"
          ),
        0
      )
    : 0;

  const storeDiscountName = (id: number) =>
    getDiscountByStoreResponse?.body.find((d) => Number(d.id) === id)?.discountName;

  // One part (check-in or checkout) of the discount on the selected bills.
  // A custom discount is saved without an id; a deleted one is just "Discount"
  // (as in the invoice history). Different discounts across bills: "Multiple".
  const discountPart = (
    amountOf: (bill: IDiscountDrawerBill, split: IBillDiscountSplit) => number,
    nameOf: (bill: IDiscountDrawerBill, split: IBillDiscountSplit) => string
  ) => {
    const withAmount = bills
      .filter((bill) => splits[bill.id])
      .map((bill) => ({ bill, split: splits[bill.id] }))
      .filter(({ bill, split }) => amountOf(bill, split) > 0);
    const names = Array.from(new Set(withAmount.map(({ bill, split }) => nameOf(bill, split))));
    return {
      amount: withAmount.reduce((acc, { bill, split }) => acc + amountOf(bill, split), 0),
      name: names.length > 1 ? "Multiple" : names[0] ?? "None",
    };
  };

  const checkInPart = discountPart(
    (_bill, split) => split.checkInAmount,
    (bill) => {
      const saved = bill.checkInDiscount;
      if (saved?.details?.discountName) return saved.details.discountName;
      if (saved?.id == null) return "Custom Discount";
      return storeDiscountName(saved.id) ?? "Discount";
    }
  );
  const checkoutPart = discountPart(
    (_bill, split) => split.checkoutAmount,
    (_bill, split) =>
      split.checkoutDiscountId == null
        ? "Custom Discount"
        : storeDiscountName(split.checkoutDiscountId) ?? "Discount"
  );
  const splitsLoaded = bills.every((bill) => splits[bill.id]);
  // Rows for the Current Discount card: the design's two rows for one
  // discount, labelled by stage when the bill has both.
  const currentParts = [
    { stage: "Check-in", ...checkInPart },
    { stage: "Checkout", ...checkoutPart },
  ].filter((part) => part.amount > 0);

  const isFixed = customDiscountType === "fixed";

  const labelSx = {
    fontSize: "var(--fs-base)",
    fontWeight: 500,
    color: Colors.TEXT_GRAY_DARK,
    mb: pxToRem(8),
  };
  const fieldSx = {
    width: "100%",
    height: pxToRem(48),
    borderRadius: pxToRem(8),
    background: Colors.WHITE,
    fontSize: "var(--fs-base)",
    color: Colors.TEXT_GRAY_DARK,
    "& .MuiOutlinedInput-notchedOutline": { borderColor: Colors.ACCENT_GRAY },
    "&:hover .MuiOutlinedInput-notchedOutline": { borderColor: Colors.COLOR_BUTTON_BORDER },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: Colors.PRIMARY, borderWidth: 1 },
  };
  // Grey "$" / "%" box on the edge of the amount field.
  const unitAdornment = (
    <InputAdornment
      position={isFixed ? "start" : "end"}
      sx={{
        height: "100%",
        maxHeight: "none",
        alignSelf: "stretch",
        m: 0,
        px: pxToRem(14),
        background: Colors.BACKGROUND_GRAY,
        [isFixed ? "borderRight" : "borderLeft"]: `1px solid ${Colors.ACCENT_GRAY}`,
        color: Colors.TEXT_GRAY_DARK,
      }}
    >
      {isFixed ? (
        <DollarSign aria-hidden size={pxToRem(18)} strokeWidth={2} absoluteStrokeWidth />
      ) : (
        <Percent aria-hidden size={pxToRem(18)} strokeWidth={2} absoluteStrokeWidth />
      )}
    </InputAdornment>
  );

  const currentRowSx = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    px: pxToRem(16),
    py: pxToRem(14),
    fontSize: "var(--fs-base)",
    color: Colors.TEXT_GRAY_DARK,
  };

  return (
    <NewCustomModalDrawer
      isOpen={isOpen}
      toggleDrawer={toggleDrawer}
      containerRef={containerRef}
      width={pxToRem(530)}
      maxWidth="90vw"
      sx={{
        background: Colors.WHITE,
        minHeight: "100%",
        width: pxToRem(530),
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: pxToRem(24),
          py: pxToRem(18),
          background: Colors.BACKGROUND_GRAY,
          borderBottom: `1px solid ${Colors.ACCENT_GRAY}`,
        }}
      >
        <Typography
          sx={{
            fontSize: "var(--fs-lg)",
            fontWeight: 600,
            color: Colors.TEXT_GRAY_DARK,
          }}
        >
          Discounts
        </Typography>
        <IconButton
          onClick={toggleDrawer}
          aria-label="Close discounts"
          sx={{ color: Colors.TEXT_GRAY_DARK }}
        >
          <ChevronRight size={pxToRem(24)} />
        </IconButton>
      </Box>

      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: pxToRem(20),
          px: pxToRem(24),
          pt: pxToRem(20),
          pb: pxToRem(24),
          overflowY: "auto",
        }}
      >
        <CustomRadioButton
          radioItems={[
            { value: "defined", label: "Predefined Discounts" },
            { value: "custom", label: "Custom Discount" },
          ]}
          orientation="row"
          justify="flex-start"
          defaultValue={discountType}
          onChange={handleTypeChange}
        />

        {discountType === "defined" ? (
          <>
            <Box>
              <Typography sx={labelSx}>Discount Name</Typography>
              <Select
                value={definedDiscountId}
                onChange={handleDefinedDiscountChange}
                IconComponent={KeyboardArrowDown}
                sx={fieldSx}
              >
                <MenuItem value={NONE_DISCOUNT_ID}>None</MenuItem>
                {definedDiscounts.map((discount) => (
                  <MenuItem key={discount.id} value={discount.id}>
                    {discount.discountName}
                  </MenuItem>
                ))}
              </Select>
              {selectedDefinedDiscount?.description && (
                <Typography
                  sx={{
                    fontSize: "var(--fs-2xs)",
                    color: Colors.TEXT_GRAY_DARK,
                    opacity: 0.7,
                    mt: pxToRem(6),
                  }}
                >
                  {selectedDefinedDiscount.description}
                </Typography>
              )}
            </Box>
            <Box>
              <Typography sx={labelSx}>Discount Amount</Typography>
              <OutlinedInput
                readOnly
                value={selectedDefinedDiscount ? formatMoney(definedPreview) : ""}
                placeholder="$0.00"
                inputProps={{ "test-name": "checkout-discount-preview" }}
                sx={{ ...fieldSx, background: Colors.BACKGROUND_GRAY }}
              />
            </Box>
          </>
        ) : (
          <>
            <Box>
              <Typography sx={labelSx}>Amount Type</Typography>
              <Select
                value={customDiscountType}
                onChange={handleCustomDiscountTypeChange}
                IconComponent={KeyboardArrowDown}
                sx={fieldSx}
              >
                <MenuItem value="percentage">Percentage</MenuItem>
                <MenuItem value="fixed">Fixed</MenuItem>
              </Select>
            </Box>
            <Box>
              <Typography sx={labelSx}>Discount Amount</Typography>
              <OutlinedInput
                value={customAmount}
                onChange={handleAmountChange}
                placeholder="0.00"
                inputProps={{ inputMode: "decimal" }}
                startAdornment={isFixed ? unitAdornment : undefined}
                endAdornment={isFixed ? undefined : unitAdornment}
                sx={{ ...fieldSx, overflow: "hidden", px: 0, "& input": { px: pxToRem(14) } }}
              />
            </Box>
          </>
        )}

        <Divider sx={{ borderColor: Colors.ACCENT_GRAY }} />

        <Box
          test-name="checkout-current-discount"
          sx={{
            border: `1px solid ${Colors.ACCENT_GRAY}`,
            borderRadius: pxToRem(8),
            overflow: "hidden",
          }}
        >
          <Typography
            sx={{
              px: pxToRem(16),
              py: pxToRem(12),
              background: Colors.BACKGROUND_GRAY,
              borderBottom: `1px solid ${Colors.ACCENT_GRAY}`,
              fontSize: "var(--fs-base)",
              fontWeight: 600,
              color: Colors.TEXT_GRAY_DARK,
            }}
          >
            Current Discount
          </Typography>
          {!splitsLoaded ? (
            <Box sx={currentRowSx}>
              <span>{splitsError ? "Couldn't load the current discount" : "Loading..."}</span>
            </Box>
          ) : currentParts.length === 0 ? (
            <>
              <Box sx={{ ...currentRowSx, borderBottom: `1px solid ${Colors.BACKSHADE_GRAY}` }}>
                <span>Discount Name</span>
                <Box component="span" sx={{ fontWeight: 600 }}>None</Box>
              </Box>
              <Box sx={currentRowSx}>
                <span>Discount Amount</span>
                <Box component="span" sx={{ fontWeight: 600 }}>{formatMoney(0)}</Box>
              </Box>
            </>
          ) : (
            currentParts.length === 1 ? (
              <>
                <Box sx={{ ...currentRowSx, borderBottom: `1px solid ${Colors.BACKSHADE_GRAY}` }}>
                  <span>Discount Name</span>
                  <Box component="span" sx={{ fontWeight: 600 }}>{currentParts[0].name}</Box>
                </Box>
                <Box sx={currentRowSx}>
                  <span>Discount Amount</span>
                  <Box component="span" sx={{ fontWeight: 600 }}>{formatMoney(currentParts[0].amount)}</Box>
                </Box>
              </>
            ) : (
              // Both a check-in and a checkout discount: one row each, so the
              // card keeps the same height.
              currentParts.map((part, index) => (
                <Box
                  key={part.stage}
                  sx={{
                    ...currentRowSx,
                    gap: pxToRem(12),
                    borderBottom:
                      index < currentParts.length - 1 ? `1px solid ${Colors.BACKSHADE_GRAY}` : "none",
                  }}
                >
                  <span>
                    {part.stage}:{" "}
                    <Box component="span" sx={{ fontWeight: 600 }}>{part.name}</Box>
                  </span>
                  <Box component="span" sx={{ fontWeight: 600 }}>{formatMoney(part.amount)}</Box>
                </Box>
              ))
            )
          )}
        </Box>

        <Typography
          test-name="checkout-discount-note"
          sx={{
            fontSize: "var(--fs-2xs)",
            color: Colors.TEXT_GRAY_DARK,
            opacity: 0.75,
          }}
        >
          {paidBillsSkipped > 0
            ? `${paidBillsSkipped} selected bill${paidBillsSkipped > 1 ? "s have" : " has"} a payment and won't get this discount. `
            : ""}
          A secondary discount can be applied at checkout. It applies only to
          the main bill, before any previous discounts.
          {checkoutPart.amount > 0
            ? " Adding one replaces the current checkout discount."
            : ""}
        </Typography>
      </Box>

      <Box sx={{ px: pxToRem(24), pb: pxToRem(24) }}>
        <CustomButton
          onClick={onClickAddDiscount}
          disabled={!canSubmit}
          sx={{
            width: "100%",
            background: Colors.PRIMARY,
            color: Colors.WHITE,
            borderRadius: pxToRem(10),
            height: pxToRem(56),
            fontSize: "var(--fs-base)",
            fontWeight: 600,
            "&:hover": { background: Colors.PRIMARY_DARK },
            "&.Mui-disabled": { background: Colors.PRIMARY, color: Colors.WHITE, opacity: 0.5 },
          }}
        >
          {submitting ? "Applying..." : "Add Discount"}
        </CustomButton>
      </Box>

      <ManagerCodeModal
        open={isAccessModalOpen}
        onClose={() => setIsAccessModalOpen(false)}
        title="Manager Approval"
        task="add a checkout discount"
        onDone={() => {
          setIsAccessModalOpen(false);
          handleAddDiscount();
        }}
        actionType="DISCOUNT_DRAWER_ACCESS"
        requestedByUserId={user?.id}
        billId={bills[0]?.id}
      />
    </NewCustomModalDrawer>
  );
};

export default DiscountDrawer;
